/**
 * SignInAffordances — the sign-in form's secondary controls (module-private to `<LoginForm>`):
 * "Forgot password?", "Remember me", the submit / cancel pair, and the company links.
 *
 * Extracted from `<LoginForm>`'s credential form so that component stays a composition of named
 * parts (each of these is a conditional the surface's seams decide).
 *
 * @module @puffin-core/login-shell/components/loginForm/SignInAffordances
 */

import PropTypes from 'prop-types';

/**
 * "Forgot password?" — the ONE reset dialog, or (W20) a link to the surface's own reset PAGE, opened
 * beside the sign-in and never replacing it.
 *
 * @param {Object} props
 * @param {Object} props.form - `useLoginForm`'s state.
 * @param {(key: string) => string} props.translate
 * @param {string} [props.href] - The reset page, when the surface has one instead of the dialog.
 * @returns {React.JSX.Element}
 */
export function ForgotPasswordPrompt({ form, translate, href }) {
  return (
    <p className="text text-sm text-muted">
      {translate('login.forgot.prompt')}{' '}
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="btn btn-link text-sm">
          {translate('login.forgot.action')}
        </a>
      ) : (
        <button type="button" className="btn btn-link text-sm" onClick={form.openPasswordReset}>
          {translate('login.forgot.action')}
        </button>
      )}
    </p>
  );
}

ForgotPasswordPrompt.propTypes = {
  form: PropTypes.shape({ openPasswordReset: PropTypes.func }).isRequired,
  translate: PropTypes.func.isRequired,
  href: PropTypes.string,
};

/**
 * "Remember me" — rendered only where something can remember (W5).
 *
 * @param {Object} props
 * @param {Object} props.form
 * @param {(key: string) => string} props.translate
 * @returns {React.JSX.Element}
 */
export function RememberMeField({ form, translate }) {
  return (
    <div className="form-group mb-md">
      <div className="flex items-center gap-sm flex-wrap">
        <input
          type="checkbox"
          id="rememberMe"
          checked={form.rememberMe}
          onChange={form.handleRememberMeChange}
          disabled={form.isLoading}
        />
        <label htmlFor="rememberMe" className="text text-sm text-nowrap">
          {translate('login.remember.plain')}
        </label>
      </div>
    </div>
  );
}

RememberMeField.propTypes = {
  form: PropTypes.shape({
    rememberMe: PropTypes.bool,
    handleRememberMeChange: PropTypes.func,
    isLoading: PropTypes.bool,
  }).isRequired,
  translate: PropTypes.func.isRequired,
};

/**
 * The submit, with the surface's own label (W8), and — when the surface has one — its cancel action.
 *
 * @param {Object} props
 * @param {Object} props.form
 * @param {(key: string) => string} props.translate
 * @param {boolean} props.disabled - The ONE submit gate `<LoginForm>` computes.
 * @param {string} [props.submitLabel]
 * @param {string} [props.submittingLabel]
 * @param {Function} [props.onCancel]
 * @param {string} [props.cancelLabel]
 * @returns {React.JSX.Element}
 */
export function SignInActions({
  form,
  translate,
  disabled,
  submitLabel,
  submittingLabel,
  onCancel,
  cancelLabel,
}) {
  return (
    <>
      <button
        type="submit"
        className="btn btn-primary btn-lg w-full"
        disabled={disabled}
        aria-busy={form.isLoading ? 'true' : 'false'}
      >
        {form.isLoading
          ? (submittingLabel ?? translate('login.submit.loading'))
          : (submitLabel ?? translate('login.submit'))}
      </button>
      {onCancel && (
        <button
          type="button"
          className="btn btn-secondary btn-lg w-full mt-md"
          onClick={onCancel}
          disabled={form.isLoading}
        >
          {cancelLabel ?? translate('login.cancel')}
        </button>
      )}
    </>
  );
}

SignInActions.propTypes = {
  form: PropTypes.shape({ isLoading: PropTypes.bool }).isRequired,
  translate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.string,
};

/**
 * "Need access? Contact your store manager" and "Not your company? Sign in elsewhere".
 *
 * @param {Object} props
 * @param {Object} props.form
 * @param {(key: string) => string} props.translate
 * @returns {React.JSX.Element}
 */
export function CompanyLinks({ form, translate }) {
  return (
    <div className="auth-card-links">
      <p className="text text-sm text-center text-muted">
        {translate('login.contact.prompt')}{' '}
        <button type="button" className="btn btn-link text-sm" onClick={form.openContactManager}>
          {translate('login.contact.action')}
        </button>
      </p>
      <p className="text text-sm text-center text-muted">
        {translate('login.notYourCompany.prompt')}{' '}
        <button type="button" className="btn btn-link text-sm" onClick={form.handleNotYourCompany}>
          {translate('login.notYourCompany.action')}
        </button>
      </p>
    </div>
  );
}

CompanyLinks.propTypes = {
  form: PropTypes.shape({
    openContactManager: PropTypes.func,
    handleNotYourCompany: PropTypes.func,
  }).isRequired,
  translate: PropTypes.func.isRequired,
};
